import React from 'react';
import { db } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

const PIECE_IMAGES = { 
    'wR': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wR.svg', 'wN': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wN.svg', 'wB': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wB.svg', 'wQ': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wQ.svg', 'wK': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wK.svg', 'wP': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/wP.svg',
    'bR': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bR.svg', 'bN': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bN.svg', 'bB': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bB.svg', 'bQ': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bQ.svg', 'bK': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bK.svg', 'bP': 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/bP.svg' 
};

const getPieceImage = (key) => {
    if (!key) return '';
    if (key.startsWith('http')) return key;
    if (PIECE_IMAGES[key]) return PIECE_IMAGES[key];
    return `https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/${key}.svg`;
};

const parseFenToBoard = (fen) => {
    const board = {};
    if (!fen) return board;
    const rows = fen.split(' ')[0].split('/');
    const cols = ['a','b','c','d','e','f','g','h'];
    rows.forEach((row, rIdx) => {
        let cIdx = 0;
        for (let char of row) {
            if (!isNaN(char)) cIdx += parseInt(char);
            else {
                board[`${cols[cIdx]}${8-rIdx}`] = { type: char.toLowerCase(), color: char === char.toUpperCase() ? 'w' : 'b' };
                cIdx++;
            }
        }
    });
    return board;
};

// 🔥 LIVE MINI BOARD FOR BOARD-BASED ARCADE MODES (MAZE, AWARENESS, DANGER)
const LiveMiniBoard = React.memo(({ mode, fen, originalTargets = [], foundTargets = [], applesLeftCount = 0 }) => {
    const render = parseFenToBoard(fen);
    const rows = [8,7,6,5,4,3,2,1]; 
    const cols = ['a','b','c','d','e','f','g','h'];

    let targetsToDraw = [];
    if (mode === 'MAZE' && originalTargets.length > 0) {
        targetsToDraw = originalTargets.slice(0, applesLeftCount || 0);
    } else if (mode === 'AWARENESS' || mode === 'DANGER') {
        targetsToDraw = originalTargets;
    }

    const isDangerMode = mode === 'DANGER';
    const borderColor = isDangerMode ? '#ef4444' : mode === 'AWARENESS' ? '#8b5cf6' : '#3b82f6';

    return (
        <div style={{
            width: '100%', 
            aspectRatio: '1/1', 
            display: 'grid', 
            gridTemplateColumns: 'repeat(8, 1fr)', 
            gridTemplateRows: 'repeat(8, 1fr)', 
            border: `2.5px solid ${borderColor}`, 
            borderRadius: '8px', 
            overflow: 'hidden', 
            boxShadow: 'var(--neu-shadow-inset)'
        }}>
            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                const id = `${col}${row}`; 
                const isDark = (rIdx + cIdx) % 2 === 1;
                const isTarget = targetsToDraw.includes(id);
                const isFound = foundTargets.includes(id);
                const pieceObj = render[id];

                let cellBg = isDark ? '#769656' : '#eeeed2';
                if (isFound) {
                    cellBg = isDangerMode ? 'rgba(239, 68, 68, 0.85)' : '#10b981';
                } else if (isTarget && (mode === 'AWARENESS' || mode === 'DANGER')) {
                    cellBg = isDangerMode ? 'rgba(245, 158, 11, 0.25)' : 'rgba(139, 92, 246, 0.25)';
                }

                return ( 
                    <div key={id} style={{
                        background: cellBg, 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        position: 'relative',
                        border: isFound ? '1.5px solid white' : 'none'
                    }}> 
                        {/* Icons */}
                        {mode === 'MAZE' && isTarget && !pieceObj && (
                            <span style={{ fontSize: '15px', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.15))' }}>🍎</span>
                        )}
                        {mode === 'AWARENESS' && isFound && (
                            <span style={{ fontSize: '14px', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }}>✅</span>
                        )}
                        {mode === 'AWARENESS' && isTarget && !isFound && (
                            <span style={{ fontSize: '12px', opacity: 0.7 }}>📍</span>
                        )}
                        {mode === 'DANGER' && isFound && (
                            <span style={{ fontSize: '14px', zIndex: 11, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}>🔥</span>
                        )}
                        {mode === 'DANGER' && isTarget && !isFound && !pieceObj && (
                            <span style={{ fontSize: '12px', opacity: 0.6 }}>⚠️</span>
                        )}

                        {/* Piece rendering */}
                        {pieceObj && (
                            <img 
                                src={PIECE_IMAGES[pieceObj.color + pieceObj.type.toUpperCase()]} 
                                alt="p" 
                                style={{ width: '85%', zIndex: 10, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} 
                            />
                        )} 
                        
                        <span style={{
                            position: 'absolute', 
                            bottom: 1, 
                            right: 2, 
                            fontSize: '8px', 
                            color: isDark || isFound ? '#ffffff' : '#475569', 
                            opacity: 0.4,
                            fontWeight: 'bold'
                        }}>
                            {id}
                        </span>
                    </div> 
                );
            }))}
        </div>
    );
}, (prevProps, nextProps) => {
    return prevProps.mode === nextProps.mode &&
           prevProps.fen === nextProps.fen &&
           prevProps.applesLeftCount === nextProps.applesLeftCount &&
           JSON.stringify(prevProps.originalTargets) === JSON.stringify(nextProps.originalTargets) &&
           JSON.stringify(prevProps.foundTargets) === JSON.stringify(nextProps.foundTargets);
});

// 🔥 LIVE PREVIEW FOR MCQ QUIZZES
const MiniMCQPreview = ({ data, stats }) => {
    const selected = stats.selected;
    const isCompleted = stats.completed;

    return (
        <div style={{
            width: '100%',
            aspectRatio: '1/1',
            background: 'var(--bg-secondary)',
            borderRadius: '10px',
            border: '2px solid #8b5cf6',
            boxShadow: 'var(--neu-shadow-inset)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '12px',
            boxSizing: 'border-box',
            gap: '8px'
        }}>
            {/* Header Question */}
            <div style={{ textAlign: 'center' }}>
                <div style={{ color: 'var(--text-primary)', fontSize: '11px', fontWeight: 'bold', lineHeight: '1.3' }}>
                    {data.question}
                </div>
                {data.image && (
                    <img 
                        src={getPieceImage(data.image)} 
                        alt="piece" 
                        style={{ width: '42px', marginTop: '4px', filter: 'drop-shadow(0 3px 5px rgba(0,0,0,0.3))' }} 
                    />
                )}
            </div>

            {/* Options Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', width: '100%' }}>
                {data.options.map(opt => {
                    const isAnswer = opt === data.answer;
                    const isUserSelected = selected === opt;

                    let bg = 'var(--bg-primary)';
                    let color = 'var(--text-primary)';
                    let border = '1px solid rgba(163, 177, 198, 0.4)';
                    let badge = '';

                    if (isUserSelected) {
                        if (isAnswer) {
                            bg = '#10b981';
                            color = 'white';
                            border = '1px solid #047857';
                            badge = '✅ ';
                        } else {
                            bg = '#ef4444';
                            color = 'white';
                            border = '1px solid #b91c1c';
                            badge = '❌ ';
                        }
                    } else if (isAnswer && isCompleted) {
                        bg = 'rgba(16, 185, 129, 0.15)';
                        color = '#10b981';
                        border = '1.5px solid #10b981';
                        badge = '✓ ';
                    }

                    return (
                        <div key={opt} style={{
                            padding: '6px 4px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            borderRadius: '6px',
                            background: bg,
                            color: color,
                            border: border,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            boxShadow: isUserSelected ? '0 2px 6px rgba(0,0,0,0.2)' : 'none',
                            transition: 'all 0.2s'
                        }}>
                            {badge}{opt}
                        </div>
                    );
                })}
            </div>

            {/* Live Student Status Badge */}
            <div style={{ 
                textAlign: 'center', 
                fontSize: '10px', 
                fontWeight: 'bold',
                padding: '4px',
                borderRadius: '6px',
                background: isCompleted ? 'rgba(16, 185, 129, 0.1)' : (selected ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)'),
                color: isCompleted ? '#10b981' : (selected ? '#ef4444' : '#ca8a04')
            }}>
                {isCompleted ? '🎉 Answered Correctly!' : (selected ? `Selected: "${selected}" (Wrong)` : '⏳ Student is thinking...')}
            </div>
        </div>
    );
};

// 🔥 LIVE PREVIEW FOR CHESS MATH
const MiniMathPreview = ({ data, stats }) => {
    const selected = stats.selected;
    const isCompleted = stats.completed;

    return (
        <div style={{
            width: '100%',
            aspectRatio: '1/1',
            background: 'var(--bg-secondary)',
            borderRadius: '10px',
            border: '2px solid #ca8a04',
            boxShadow: 'var(--neu-shadow-inset)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '12px',
            boxSizing: 'border-box',
            gap: '8px'
        }}>
            {/* Equation Header */}
            <div style={{ textAlign: 'center' }}>
                <div style={{ color: '#ca8a04', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                    {data.title || 'Chess Math'}
                </div>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px', 
                    background: 'var(--bg-primary)', 
                    padding: '6px 8px', 
                    borderRadius: '8px', 
                    border: '1px solid rgba(163, 177, 198, 0.4)', 
                    flexWrap: 'wrap', 
                    justifyContent: 'center' 
                }}>
                    {data.pieces.map((item, idx) => (
                        item === '+' || item === '-' ? 
                        <span key={idx} style={{ fontSize: '16px', color: 'var(--text-primary)', fontWeight: 'bold' }}>{item}</span> :
                        <img 
                            key={idx} 
                            src={getPieceImage(item)} 
                            alt="piece" 
                            style={{ width: '24px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} 
                        />
                    ))}
                    <span style={{ fontSize: '16px', color: '#ca8a04', fontWeight: 'bold' }}>=?</span>
                </div>
            </div>

            {/* Math Options Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', width: '100%' }}>
                {data.options.map(opt => {
                    const isAnswer = opt === data.answer;
                    const isUserSelected = selected === opt;

                    let bg = 'var(--bg-primary)';
                    let color = 'var(--text-primary)';
                    let border = '1px solid rgba(163, 177, 198, 0.4)';
                    let badge = '';

                    if (isUserSelected) {
                        if (isAnswer) {
                            bg = '#10b981';
                            color = 'white';
                            border = '1px solid #047857';
                            badge = '✅ ';
                        } else {
                            bg = '#ef4444';
                            color = 'white';
                            border = '1px solid #b91c1c';
                            badge = '❌ ';
                        }
                    } else if (isAnswer && isCompleted) {
                        bg = 'rgba(16, 185, 129, 0.15)';
                        color = '#10b981';
                        border = '1.5px solid #10b981';
                        badge = '✓ ';
                    }

                    return (
                        <div key={opt} style={{
                            padding: '6px 4px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            textAlign: 'center',
                            borderRadius: '6px',
                            background: bg,
                            color: color,
                            border: border,
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            boxShadow: isUserSelected ? '0 2px 6px rgba(0,0,0,0.2)' : 'none',
                            transition: 'all 0.2s'
                        }}>
                            {badge}{opt} Pts
                        </div>
                    );
                })}
            </div>

            {/* Live Student Status Badge */}
            <div style={{ 
                textAlign: 'center', 
                fontSize: '10px', 
                fontWeight: 'bold',
                padding: '4px',
                borderRadius: '6px',
                background: isCompleted ? 'rgba(16, 185, 129, 0.1)' : (selected ? 'rgba(239, 68, 68, 0.1)' : 'rgba(245, 158, 11, 0.1)'),
                color: isCompleted ? '#10b981' : (selected ? '#ef4444' : '#ca8a04')
            }}>
                {isCompleted ? `🎉 Correct! (${data.answer} Points)` : (selected ? `Selected: ${selected} Pts (Wrong)` : '⏳ Solving equation...')}
            </div>
        </div>
    );
};

export default function ArcadeMonitor({ activeClass, usersMap, stopArcade }) {
    if (!activeClass || !activeClass.arcadeData) return null;
    
    const { question, title, category, levels, mode, targets } = activeClass.arcadeData;
    const studentsList = activeClass.students || [];

    // Calculate aggregated live stats for Coach
    let completedCount = 0;
    let inProgressCount = 0;
    let totalMistakes = 0;

    studentsList.forEach(email => {
        const safeEmail = email.replace(/\./g, '_');
        const stats = activeClass.studentArcadeStats?.[safeEmail];
        if (stats?.completed) completedCount++;
        else inProgressCount++;
        if (stats?.mistakes) totalMistakes += stats.mistakes;
    });

    const handleRelaunchTask = async () => {
        try {
            await updateDoc(doc(db, "classes", activeClass.id), {
                'arcadeData.startTime': Date.now(),
                studentArcadeStats: {},
                studentFens: {}
            });
            toast.success("Relaunched task for all students! 🚀");
        } catch (e) {
            toast.error("Failed to relaunch task.");
        }
    };

    const handleSendCoachHint = async () => {
        const hintText = activeClass.arcadeData.hint || "Look carefully at the screen!";
        try {
            await updateDoc(doc(db, "classes", activeClass.id), {
                coachNotice: `💡 COACH HINT: ${hintText}`
            });
            toast.success("Sent hint to all students! 💡");
        } catch (e) {
            toast.error("Failed to send hint.");
        }
    };

    const modeLabels = {
        'AWARENESS': '📍 Board Awareness',
        'DANGER': '⚠️ Danger Zone',
        'MCQ': '🧠 Multiple Choice Quiz',
        'MATH': '🧮 Chess Math',
        'MAZE': '🍎 Apple Maze'
    };

    const modeColors = {
        'AWARENESS': '#8b5cf6',
        'DANGER': '#ef4444',
        'MCQ': '#3b82f6',
        'MATH': '#ca8a04',
        'MAZE': '#10b981'
    };

    const currentModeColor = modeColors[mode] || '#8b5cf6';

    return (
        <div style={{
            width: '100%', 
            background: 'var(--bg-primary)', 
            padding: '20px', 
            borderRadius: '16px', 
            border: `1.5px solid ${currentModeColor}`, 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%', 
            boxSizing: 'border-box', 
            boxShadow: '0 15px 40px rgba(0,0,0,0.4)',
            overflow: 'hidden'
        }}>
            
            {/* Top Control Header */}
            <div style={{
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '15px', 
                flexWrap: 'wrap', 
                gap: '12px', 
                background: 'var(--bg-secondary)', 
                padding: '12px 18px', 
                borderRadius: '12px', 
                border: `1px solid rgba(163, 177, 198, 0.3)`
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ fontSize: '28px', filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.2))' }}>🎮</div>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                background: currentModeColor, 
                                color: 'white', 
                                fontSize: '10px', 
                                fontWeight: '900', 
                                padding: '2px 8px', 
                                borderRadius: '12px',
                                textTransform: 'uppercase'
                            }}>
                                {modeLabels[mode] || mode}
                            </span>
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Live Monitor</span>
                        </div>
                        <h2 style={{ margin: '3px 0 0 0', color: 'var(--text-primary)', fontSize: '18px', fontWeight: 'bold' }}>
                            {category === 'MAZE' ? title : (question || title)}
                        </h2>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button 
                        onClick={handleRelaunchTask}
                        style={{
                            padding: '8px 14px', 
                            background: 'var(--bg-primary)', 
                            color: 'var(--text-primary)', 
                            border: '1.5px solid white', 
                            borderRadius: '8px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer', 
                            fontSize: '11px',
                            boxShadow: 'var(--neu-shadow)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                        🔄 Relaunch Task
                    </button>
                    <button 
                        onClick={handleSendCoachHint}
                        style={{
                            padding: '8px 14px', 
                            background: 'rgba(245, 158, 11, 0.1)', 
                            color: '#ca8a04', 
                            border: '1px solid #ca8a04', 
                            borderRadius: '8px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer', 
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}
                    >
                        💡 Send Hint
                    </button>
                    <button 
                        onClick={stopArcade} 
                        style={{
                            padding: '8px 16px', 
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer', 
                            fontSize: '11px',
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '6px', 
                            boxShadow: '0 4px 10px rgba(239, 68, 68, 0.3)'
                        }}
                    >
                        ⬅️ Master Board
                    </button>
                </div>
            </div>

            {/* Aggregated Class Stats Bar */}
            <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '15px',
                flexWrap: 'wrap',
                background: 'rgba(0,0,0,0.1)',
                padding: '8px 12px',
                borderRadius: '10px',
                border: '1px solid rgba(163, 177, 198, 0.25)',
                fontSize: '11px',
                fontWeight: 'bold'
            }}>
                <span style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    👥 Students in Class: <strong style={{ color: '#3b82f6' }}>{studentsList.length}</strong>
                </span>
                <span style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    ✅ Solved: <strong style={{ color: '#10b981' }}>{completedCount}</strong>
                </span>
                <span style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    ⏳ Solving: <strong style={{ color: '#ca8a04' }}>{inProgressCount}</strong>
                </span>
                <span style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    ⚠️ Total Mistakes: <strong style={{ color: '#ef4444' }}>{totalMistakes}</strong>
                </span>
            </div>

            {/* Grid of Live Student Screen Cards */}
            <div style={{
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', 
                gap: '15px', 
                overflowY: 'auto', 
                flex: 1, 
                paddingRight: '5px' 
            }}>
                {studentsList.length === 0 && (
                    <div style={{
                        color: 'var(--text-secondary)', 
                        gridColumn: '1/-1', 
                        textAlign: 'center', 
                        padding: '40px', 
                        background: 'var(--bg-primary)', 
                        borderRadius: '16px', 
                        border: '1px dashed rgba(163, 177, 198, 0.4)' 
                    }}>
                        No students currently in this class to monitor.
                    </div>
                )}

                {studentsList.map(email => {
                    const safeEmail = email.replace(/\./g, '_');
                    const isOnline = activeClass.presence && (Date.now() - activeClass.presence[safeEmail] < 45000);
                    const stats = activeClass.studentArcadeStats?.[safeEmail] || { level: 0, stars: 0, mistakes: 0, completed: false, remainingApples: 0, foundTargets: [], selected: null };
                    
                    const defaultFen = mode === 'DANGER' ? activeClass.arcadeData.fen : '8/8/8/8/8/8/8/8 w - - 0 1';
                    const currentFen = activeClass.studentFens?.[safeEmail] || (category === 'MAZE' && levels ? levels[stats.level]?.fen : defaultFen);
                    
                    const originalTargets = category === 'MAZE' && levels ? levels[stats.level]?.targets : (targets || []);
                    const foundTargets = stats.foundTargets || [];

                    return (
                        <div key={email} style={{
                            background: 'var(--bg-primary)', 
                            padding: '12px', 
                            borderRadius: '14px', 
                            border: stats.completed ? '2px solid #10b981' : '1px solid rgba(163, 177, 198, 0.4)', 
                            opacity: isOnline ? 1 : 0.65, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '8px', 
                            boxShadow: stats.completed ? '0 0 15px rgba(16,185,129,0.2)' : 'var(--neu-shadow)', 
                            transition: 'all 0.25s' 
                        }}>
                            {/* Student Card Top Bar */}
                            <div style={{
                                color: 'var(--text-primary)', 
                                fontWeight: 'bold', 
                                fontSize: '13px', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center' 
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <span>👤</span> 
                                    <span>{usersMap[email] || email.split('@')[0]}</span>
                                </div>
                                <span style={{
                                    width: '8px', 
                                    height: '8px', 
                                    background: isOnline ? '#10b981' : '#64748b', 
                                    borderRadius: '50%', 
                                    display: 'inline-block', 
                                    flexShrink: 0
                                }}></span>
                            </div>

                            {/* 🔥 LIVE SCREEN MIRRORING (Exact format of what student sees) */}
                            <div>
                                {mode === 'MCQ' ? (
                                    <MiniMCQPreview data={activeClass.arcadeData} stats={stats} />
                                ) : mode === 'MATH' ? (
                                    <MiniMathPreview data={activeClass.arcadeData} stats={stats} />
                                ) : (
                                    <LiveMiniBoard 
                                        mode={mode} 
                                        fen={currentFen} 
                                        originalTargets={originalTargets} 
                                        foundTargets={foundTargets}
                                        applesLeftCount={stats.remainingApples} 
                                    />
                                )}
                            </div>
                            
                            {/* Card Footer Info */}
                            {category === 'MAZE' ? (
                                <div style={{
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    gap: '4px', 
                                    background: 'var(--bg-secondary)', 
                                    padding: '8px 10px', 
                                    borderRadius: '8px' 
                                }}>
                                    <div style={{ color: '#2563eb', fontSize: '11px', fontWeight: 'bold', textAlign: 'center' }}>
                                        {stats.completed ? '🎉 Maze Finished!' : `Level ${stats.level + 1} of ${levels ? levels.length : 0}`}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: 'bold' }}>
                                        <span style={{ color: '#ca8a04' }}>🍎 {stats.stars} Stars</span>
                                        <span style={{ color: stats.mistakes > 0 ? '#ef4444' : 'var(--text-secondary)' }}>
                                            Mistakes: {stats.mistakes}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div style={{
                                    display: 'flex', 
                                    justify: 'space-between', 
                                    alignItems: 'center', 
                                    background: 'var(--bg-secondary)', 
                                    padding: '6px 10px', 
                                    borderRadius: '8px',
                                    fontSize: '11px',
                                    fontWeight: 'bold'
                                }}>
                                    <span style={{ color: stats.completed ? '#10b981' : '#ca8a04' }}>
                                        {stats.completed ? '✅ Finished' : '⏳ In Progress'}
                                    </span>
                                    {stats.mistakes > 0 && (
                                        <span style={{ color: '#ef4444' }}>
                                            Mistakes: {stats.mistakes}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
